"use client";

import { useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { dockPad } from "@/lib/home-flags";
import { AppShell } from "@/components/AppShell";
import { VestraBadge } from "@/components/ui/VestraBadge";
import { trackCtaClick } from "@/components/marketing/CtaLink";
import Main from "@/components/atoms/main";
import Tabs, { ITabItem } from "@/components/atoms/tabs";

const TABS: Array<ITabItem> = [
  { id: "abilities", slug: "/abilities", label: "Abilities" },
  { id: "skills", slug: "/skills", label: "Skills" },
  { id: "agents", slug: "/agents", label: "Agents" },
];

function deriveActiveTab(
  pathname: string | null,
  tabs: Array<ITabItem>
): string {
  if (!pathname) return tabs[0].id;
  const segment = pathname.split("/")[1];
  return tabs.find((t) => t.id === segment)?.id ?? tabs[0].id;
}

export function MarketplaceShell({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const pathname = usePathname();
  const activeTab = deriveActiveTab(pathname, TABS);
  const isDetailPage = pathname ? pathname.split("/").length > 2 : false;

  return (
    <div className="flex flex-col min-h-0 h-full bg-bg-base text-fg-default antialiased">
      <header className="h-(--site-header-height) sticky top-0 z-1 flex items-center border-b border-border-subtle bg-bg-base/80 backdrop-blur-md">
        <div className="w-full max-w-[1200px] mx-auto px-4 flex items-center gap-5 justify-between">
          <Link
            href="/"
            aria-label="Homepage"
            className="inline-flex items-center gap-2 text-fg-default"
          >
            <VestraBadge className="size-6" />
            <span className="text-4.5 font-semibold tracking-tight">
              Vestra
            </span>
          </Link>

          <div className="flex items-center gap-2.5">
            {user ? (
              <Link
                href="/home"
                className="inline-flex h-8 items-center rounded-full border border-border-strong px-3.5 text-3.5 font-medium text-fg-default hover:bg-bg-muted"
              >
                Open app
              </Link>
            ) : (
              <>
                <Link
                  href="/login"
                  className="inline-flex h-8 items-center rounded-full px-3.5 text-3.5 font-medium text-fg-default hover:bg-bg-muted"
                >
                  Sign in
                </Link>
                <Link
                  href="/login"
                  className="inline-flex h-8 items-center rounded-full bg-fg-default px-3.5 text-3.5 font-medium text-fg-inverse hover:opacity-90"
                  onClick={() => trackCtaClick("nav", "marketplace")}
                >
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <Main maxWidth="max-w-[1200px]">
        <div className="flex flex-col bg-bg-base gap-y-7.5 max-sm:gap-y-5.5">
          {isDetailPage ? null : (
            <div>
              <Tabs
                active={activeTab}
                items={TABS}
                ariaLabel="Library sections"
                clickContext="library"
              />
            </div>
          )}

          {children}
        </div>
      </Main>
    </div>
  );
}

/**
 * Auth-aware chrome for the public `(marketplace)` route group (/abilities,
 * /skills, /agents and their detail pages).
 *
 * - **Anonymous (or auth still loading):** the public marketplace top bar.
 *   Server-side there is no Supabase cookie (auth is client-only), so anon HTML
 *   is the SSR-correct default.
 * - **Signed-in on a LIST ROOT** (/abilities, /skills, /agents): redirected to
 *   the unified in-`(app)` library tab route `/library/<tab>`. This is the
 *   load-bearing change: the in-app shell now lives in exactly ONE place (the
 *   `(app)` group), so moving between the five sidebar tabs is a content-only
 *   swap and never remounts `AppShell`/`AuthGuard` (no gate re-fetch, no loader
 *   flash, no prefetch burst). The public list routes stay intact for anon/SEO.
 * - **Signed-in on a DETAIL route** (/abilities/[slug], agents/new, nested):
 *   still the in-app sidebar shell, as before — a deliberate context switch,
 *   and a signed-in user must never see the anon "Sign in" CTAs.
 *
 * The redirect MUST be client-side: auth is client-only in this group, so a
 * server `redirect()` can't see the session and would SSR-flash anon then bounce.
 */

export default function MarketplaceLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  // Auth-aware chrome: logged-in users get the in-app sidebar shell (their
  // original screen), anon visitors get the public marketplace top bar.
  const { user, hasAuthCookie } = useAuth();
  const pathname = usePathname() ?? "";
  const router = useRouter();
  const listRootTab = TABS.find((tab) => tab.slug === pathname)
    ? pathname
    : undefined;

  const isLikelyLoggedIn = user || hasAuthCookie;

  useEffect(() => {
    // If they are likely logged in and on a root tab, redirect immediately
    if (isLikelyLoggedIn && listRootTab) {
      router.replace(`/library${listRootTab}`);
    }
  }, [isLikelyLoggedIn, listRootTab, router]);

  if (isLikelyLoggedIn && !listRootTab) {
    return (
      <AppShell>
        <Main maxWidth>
          <div
            className={`flex flex-col bg-bg-base gap-y-7.5 max-sm:gap-y-5.5 ${dockPad(
              pathname
            )}`}
          >
            {children}
          </div>
        </Main>
      </AppShell>
    );
  }

  return <MarketplaceShell>{children}</MarketplaceShell>;
}
